import {useErrorHandler} from "react-error-boundary";
import {useUserContext} from "../../types/contexts/UserContext";
import {useSystemContext} from "../../types/contexts/SystemContext";
import {memo, useCallback, useMemo} from "react";
import {API} from "../../types/API";
import {AxiosResponse} from "axios";
import {User} from "../../types/User";
import {APILink} from "../../types/APILink";
import FooterMenu from "./FooterMenu";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import UserAvatar from "../UserAvatar";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store";
import SidebarIconTooltip from "./SidebarIconTooltip";
import {setMiniSidebar} from "../../reducers/sidebar";

const ProfilePanel = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const system = useSystemContext();
    const navigate = useNavigate();
    const sidebarTitle = useSelector((state: RootState) => state.sidebar.title);

    const logOut = useCallback(() => {
        API.logout().then(() => {
            localStorage.removeItem('userToken');
            localStorage.removeItem('tokenExpiresAt');
            window.location.replace('/app/admin');
        }).catch(e => {
            handleError(e);
            API.removeURL(`get${APILink.LOGOUT}`);
        });
    }, [handleError]);
    const getFullName = () => {
        return user ? user.user.first_name + ' ' + user.user.last_name : '';
    }

    const getAvatar = () => {
        return user ? user.user.image : '';
    }

    const changeTheme = useCallback((name: string | number) => {
        API.updateUserMe({theme: name}).then((response: AxiosResponse) => {
            user?.setUser(response.data.data as User);
        }).catch(e => {
            handleError(e);
            API.removeURL(`patch${APILink.ME}`);
        });
    }, [user, handleError]);

    const changeLanguage = useCallback((code: string | number) => {
        API.updateUserMe({language: code}).then((response: AxiosResponse) => {
            user?.setUser(response.data.data as User);
        }).catch(e => {
            handleError(e);
            API.removeURL(`patch${APILink.ME}`);
        });
    }, [user, handleError]);

    const changeProfileAction = useCallback((code: string | number) => {
        const codeName = typeof code !== 'string' ? code.toString() : code;
        switch (codeName) {
            case 'profile':
                navigate('/app/admin/profile')
                break;
            case 'password':
                navigate('/app/admin/profile/password')
                break;
            case 'logout':
                logOut();
                break;
            default:
                break;
        }
    }, [logOut]);


    const onClickSidebarIcon = useCallback(() => {
        dispatch(setMiniSidebar(true));
    }, [dispatch]);

    return useMemo(() => {
        if (!system || !system.system || !system.system.themes)
            return <></>
        return <div
            className="w-full rounded-xl p-2 flex justify-between items-center">
            <div className="flex items-center md:w-full">
                <SidebarIconTooltip
                    text={t('admin.sidebar.expand', {defaultValue: 'Expand sidebar'})}
                    onClick={onClickSidebarIcon}
                    iconClassName="md:hidden mr-2"
                    className={`hover:bg-blue-gray-50 dark:hover:bg-[#eceff1cc] dark:hover:text-[#263239] dark:hover:fill-[#263239] p-1 rounded-lg cursor-e-resize md-hidden-important`}
                    withLogo={false}
                />
                <h2 className="font-bold text-xl md:text-2xl lg:text-4xl">{sidebarTitle}</h2>
            </div>
            <div className="flex flex-row gap-0 justify-end">
                <FooterMenu
                    className="text-blue-gray-900 dark:text-gray-500"
                    onClick={changeTheme}
                    list={system.system.themes}
                    selected={''}
                    icon={<FontAwesomeIcon className="text-blue-gray-900 dark:text-gray-500 h-5 w-5 mr-2"
                                           icon={solid('palette')}/>}/>

                <FooterMenu
                    className="text-blue-gray-900 dark:text-gray-500"
                    onClick={changeLanguage}
                    list={system.system.languages.map((i) => {
                        return {
                            ...i,
                            icon: <img src={`${window.location.origin}/images/countries/${i.code}.png`}
                                       className="h-3 max-w-[1.2rem] inline-block mr-2"/>
                        }
                    })}
                    selected={''}
                    icon={<FontAwesomeIcon className="text-blue-gray-900 dark:text-gray-500 h-5 w-5 mr-2"
                                           icon={solid('globe')}/>}/>

                <FooterMenu
                    buttonClassName="w-[max-content!important]"
                    className="text-blue-gray-900 dark:text-gray-500"
                    onClick={changeProfileAction}
                    selected={''}
                    list={[
                        {name: t('admin.menu.profile'), code: 'profile'},
                        {name: t('admin.menu.change_password'), code: 'password'},
                        {name: t('admin.menu.log_out'), code: 'logout'},
                    ]}
                    icon={<div
                        className="text-blue-gray-900 dark:text-gray-50 flex items-center justify-center flex-row md:gap-3">
                        <UserAvatar className="text-blue-gray-900 dark:text-gray-500" image={getAvatar()}
                                    size="xs"/>
                        <span className="md:block hidden">
                            {getFullName()}
                        </span>
                    </div>}/>
            </div>
        </div>
    }, [user, system, sidebarTitle]);
}

export default memo(ProfilePanel);
